import 'react-pdf/dist/Page/TextLayer.css';

import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticated } from '@qapline/api';
import {
    INVOICE_STATUS_TO_COLOR,
    TExtendedInvoice,
    useGetAttachmentFileQuery,
    useListAttachmentsQuery,
} from '@qapline/core';
import { useDocumentTitle } from '@qapline/helpers';
import { ELanguage } from '@qapline/translations';
import { Pill } from '@qapline/ui';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { IconInfo, TrashIcon } from 'assets/icons';
import { config } from 'config';
import { DownloadAttachmentFileButton } from 'core/attachments/components/DownloadAttachmentFileButton';
import { PrintAttachmentFileButton } from 'core/attachments/components/PrintAttachmentFileButton';
import { Currency } from 'modules/common';
import { useGetOneCustomerQuery } from 'modules/customers/services';
import { InvoiceBasicForm } from 'modules/form';
import {
    useDeleteInvoiceMutation,
    useGetInvoiceQuery,
    useMailInvoiceMutation,
    useUpdateInvoiceMutation,
} from 'modules/invoicing/api';
import { useInvoicePdfPreview } from 'modules/invoicing/hooks';
import { InvoiceFormSchema } from 'modules/invoicing/types';
import { buildUpdateInvoicePayload } from 'modules/invoicing/utils';
import { PageContent, Subheader } from 'modules/layout';
import { BackButton, Button, NotFoundPage, Spinner } from 'modules/ui';
import { IMailModalProps, MailModal } from 'modules/ui/components/MailModal';
import { TMailModalFormSchema } from 'modules/ui/components/MailModal/MailModal.types';

import { Modal } from '../../../ui/components/Modal/Modal';
import { InvoiceDocument } from '../InvoiceDocument';
import { MailInvoiceAttachmentsButton } from '../MailInvoiceAttachmentsButton';

import styles from './InvoicingDetailPage.module.scss';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const toFormSchema = (invoice: TExtendedInvoice): InvoiceFormSchema => ({
    invoice_id: invoice.invoice_id,
    paid: invoice.paid,
    paidAmount: invoice.paidAmount === null ? null : String(invoice.paidAmount),
    paymentStatus: invoice.paymentStatus,
    paymentConfirmationDate:
        invoice.paymentConfirmationDate == null ? null : String(invoice.paymentConfirmationDate.unix()),
    paidStateChangedBy: invoice.paidStateChangedBy,
    paidStateChangedBy_id: invoice.paidStateChangedBy_id,
    exported: invoice.dateExported != null,
    number: invoice.number,
    customer_company: invoice.customer?.name ?? '',
    currency: invoice.currency as Currency,
    dateIssue: invoice.dateIssue == null ? '' : String(invoice.dateIssue.unix()),
    dateOrder: invoice.dateOrder == null ? '' : String(invoice.dateOrder.unix()),
    datePoint: invoice.datePoint == null ? '' : String(invoice.datePoint.unix()),
    dateDue: invoice.dateDue == null ? '' : String(invoice.dateDue.unix()),
    constantSymbol: invoice.constantSymbol ?? '',
    language: invoice.language.languageCode as 'cs' | 'en' | 'de',
    commissions: (invoice.commissions ?? []).map((commission) => {
        const firstLoading =
            commission.loadings.find((l) => l.commissionLoading_id === commission.firstLoading_id) ??
            commission.loadings[0];
        const lastDischarge =
            commission.discharges.find((d) => d.commissionDischarge_id === commission.lastDischarge_id) ??
            commission.discharges[commission.discharges.length - 1];
        const loadingDates = commission.loadings
            .map((l) => (l.dateFrom ? l.dateFrom.unix() : null))
            .filter((d): d is number => d !== null);
        const dischargeDates = commission.discharges
            .map((d) => (d.dateFrom ? d.dateFrom.unix() : null))
            .filter((d): d is number => d !== null);

        return {
            commission_id: commission.commission_id,
            commissionNumber: commission.qid ?? '',
            qid: commission.qid,
            vat: commission.vat === null ? '21' : String(commission.vat),
            priceCustomer: Number(commission.customerPrice ?? 0),
            currencyCustomer: (commission.customerCurrency ??
                'CZK') as InvoiceFormSchema['commissions'][number]['currencyCustomer'],
            customer_company: invoice.customer?.name ?? '',
            orderDate: commission.orderDate == null ? '0' : String(commission.orderDate.unix()),
            pickupDate: firstLoading?.dateFrom ? firstLoading.dateFrom.unix() : null,
            pointDate: lastDischarge?.dateFrom ? lastDischarge.dateFrom.unix() : null,
            loading_city_string: firstLoading?.location?.city ?? '',
            discharge_city_string: lastDischarge?.location?.city ?? '',
            loading_date: loadingDates,
            discharge_date: dischargeDates,
        };
    }),
    companyRegistrationNumber: invoice.customer?.companyRegistrationNumber ?? null,
    customer_id: invoice.customer?.customer_id ?? null,
    deleted: invoice.deleted,
    invoiceAttachmentId: invoice.invoiceAttachment_id,
});

export const InvoicingDetailPage = () => {
    const { invoiceId: invoiceIdParam } = useParams();
    const invoiceId = Number(invoiceIdParam);
    const { t } = useTranslation();
    const { t: tCommon } = useTranslation('common');
    const navigate = useNavigate();
    useDocumentTitle(t('nav.invoicing'));

    const {
        data: invoice,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetInvoiceQuery({ invoiceId }, { skip: !invoiceId });
    const [mailInvoice, { isLoading: isPostingEmail }] = useMailInvoiceMutation();
    const [updateInvoice, { isLoading: isSubmittingFormLoading }] = useUpdateInvoiceMutation();
    const [deleteInvoice, { isLoading: isSubmittingDeleting }] = useDeleteInvoiceMutation();
    const { data: attachments } = useListAttachmentsQuery(
        {
            invoiceId: invoice?.invoice_id ?? 0,
            withCommissionsDeliveryNotes: true,
        },
        { skip: !invoice?.invoice_id },
    );
    const { data: invoiceFile, isFetching: isFetchingInvoiceFile } = useGetAttachmentFileQuery(
        { attachmentId: invoice?.invoiceAttachment_id ?? 0 },
        { skip: !invoice?.invoiceAttachment_id },
    );

    // the form derives dateDue from dateIssue + customer.defaultDueDate, so it needs the full customer
    const { data: customer } = useGetOneCustomerQuery(
        { id: String(invoice?.customer?.customer_id ?? 0) },
        { skip: !invoice?.customer?.customer_id },
    );

    const [isSomeValueChanged, setIsSomeValueChanged] = useState<boolean>(false);
    const [previewValues, setPreviewValues] = useState<InvoiceFormSchema | null>(null);

    // Only an edited form is worth previewing — while it is pristine the stored PDF *is* the invoice,
    // right down to who issued it, so nothing is re-rendered.
    const { blobUrl: previewBlobUrl, isRendering: isRenderingPreview } = useInvoicePdfPreview({
        values: isSomeValueChanged ? previewValues : null,
        invoiceId,
    });
    const [showSendEmailConfirmationModal, setShowSendEmailConfirmationModal] = useState(false);
    const [showRemoveConfirmationPopup, setShowRemoveConfirmationPopup] = useState(false);

    const { user } = useAuthenticated();

    const formData = useMemo(() => (invoice ? toFormSchema(invoice) : undefined), [invoice]);

    const mailAttachments = useMemo((): TMailModalFormSchema['attachments'] => {
        const attachmentsToInclude: TMailModalFormSchema['attachments'] = [];
        if (attachments?.length) {
            attachments.forEach(({ attachment_id, name }) => {
                attachmentsToInclude.push({ label: name, value: attachment_id });
            });
        }
        return attachmentsToInclude;
    }, [attachments]);

    const handleRemoveInvoice = async () => {
        try {
            await deleteInvoice({ invoiceId }).unwrap();
            toast.success(t('invoicing.removed'));
            navigate(config.routes.invoice.overview);
        } catch (error) {
            toast.error(t('form.saveError'));
        } finally {
            setShowRemoveConfirmationPopup(false);
        }
    };

    if (isError) {
        if ('status' in (error as FetchBaseQueryError)) {
            const fetchError = error as FetchBaseQueryError;
            if (fetchError.status === 404) {
                return <NotFoundPage routeKey="invoices" />;
            }
        }
    }

    if (isLoading || isFetching || !invoiceId || !invoice || !user || !formData) return <Spinner fullScreen />;

    const prepareData = async (
        data: InvoiceFormSchema,
        _editedBy: unknown,
        _isAlreadyCreated: boolean,
        _oldData?: InvoiceFormSchema,
    ) => {
        const body = buildUpdateInvoicePayload(data, {
            dateExported: invoice.dateExported ? invoice.dateExported.unix() : null,
        });

        try {
            await updateInvoice({
                invoiceId,
                body,
            }).unwrap();
            toast.success(t('invoicing.updated'));
            navigate(config.routes.invoice.overview);
        } catch {
            toast.error(t('form.saveError'));
        }
    };

    const handleEmail: IMailModalProps['onSubmit'] = async ({ message, recipients }) => {
        try {
            const data = await mailInvoice({
                emails: recipients,
                invoiceId,
                message,
                lang: invoice.language.languageCode as ELanguage,
            }).unwrap();

            const translatedMessage = data.message === 'invoiceEmail.sent' ? t('invoiceEmail.sent') : data.message;
            if (data.rejectedEmails && data.rejectedEmails.length > 0) {
                toast.error(translatedMessage, { duration: 10000 });
            } else {
                toast.success(translatedMessage);
                setShowSendEmailConfirmationModal(false);
            }
        } catch (error) {
            toast.error(t('table.actions.email.message.error'));
        }
    };

    return (
        <PageContent
            fullWidth
            scrollable
            subheader={
                <Subheader
                    startSlot={<BackButton />}
                    titleSlot={
                        <Pill color={INVOICE_STATUS_TO_COLOR[invoice.status]}>
                            {tCommon(`entities.invoice.status.${invoice.status}`)}
                        </Pill>
                    }
                    endSlot={
                        <div className={styles.actionButtons}>
                            {invoice.dateExported === null && (
                                <Button
                                    onClick={() => setShowRemoveConfirmationPopup(true)}
                                    isLoading={isSubmittingDeleting}
                                    disabled={isSubmittingDeleting}
                                    className={styles.deleteBtn}
                                    type="button"
                                    variant="primary"
                                    danger
                                >
                                    <TrashIcon />
                                </Button>
                            )}
                            {invoice.invoiceAttachment_id && (
                                <>
                                    <PrintAttachmentFileButton
                                        attachmentId={invoice.invoiceAttachment_id}
                                        disabled={isSomeValueChanged}
                                    />
                                    <DownloadAttachmentFileButton
                                        attachmentId={invoice.invoiceAttachment_id}
                                        disabled={isSomeValueChanged}
                                    />
                                </>
                            )}
                            <MailInvoiceAttachmentsButton
                                invoiceId={invoice.invoice_id}
                                disabled={isSomeValueChanged}
                            />
                            <Button
                                isLoading={isSubmittingFormLoading}
                                disabled={isSubmittingFormLoading}
                                form="invoice-form"
                                type="submit"
                            >
                                {t('invoicing.form.save')}
                            </Button>
                        </div>
                    }
                    title={`${t('invoicing.form.title')} ${invoice.number || ''}`}
                />
            }
        >
            <div className={styles.container}>
                <div className={styles.form}>
                    <InvoiceBasicForm
                        fetchedData={formData}
                        customer={customer}
                        prepareData={prepareData}
                        setIsSomeValueChanged={setIsSomeValueChanged}
                        invoiceId={invoice.invoice_id}
                        invoiceStatus={invoice.status}
                        onValuesChange={setPreviewValues}
                    />
                </div>
                <InvoiceDocument
                    // Keep the saved PDF up until the first preview arrives, so an edit does not blank
                    // out the document while the new one renders.
                    blobUrl={
                        isSomeValueChanged && previewBlobUrl !== null ? previewBlobUrl : (invoiceFile?.data ?? null)
                    }
                    loading={isFetchingInvoiceFile}
                    isRefreshing={isRenderingPreview}
                />
            </div>
            {showSendEmailConfirmationModal && (
                <MailModal
                    title={t('invoicing.sendEmailModal.label')}
                    isSubmitting={isPostingEmail}
                    onSubmit={handleEmail}
                    initialValues={{
                        attachments: mailAttachments,
                        message: t('invoicing.sendEmailModal.message', {
                            lng: invoice.language.languageCode,
                        }),
                        recipients: [
                            ...(invoice.customer?.billingEmail
                                ? [{ value: invoice.customer.billingEmail, label: invoice.customer.billingEmail }]
                                : []),
                        ],
                    }}
                    infoSlot={
                        <div className={styles.emailModalInfo}>
                            <IconInfo color="#738291" /> {t('invoicing.sendEmailModal.info')}
                        </div>
                    }
                    onOpenChange={setShowSendEmailConfirmationModal}
                />
            )}
            {showRemoveConfirmationPopup && (
                <Modal
                    onClick={() => setShowRemoveConfirmationPopup(false)}
                    label={t('invoicing.modalRemove.label')}
                    cancelComponent={
                        <Button
                            className={styles.btn}
                            type="button"
                            variant="secondary"
                            onClick={() => setShowRemoveConfirmationPopup(false)}
                        >
                            {t('invoicing.modalRemove.cancel')}
                        </Button>
                    }
                    approveComponent={
                        <Button
                            className={styles.btn}
                            type="button"
                            danger
                            variant="primary"
                            onClick={handleRemoveInvoice}
                        >
                            {t('invoicing.modalRemove.approve')}
                        </Button>
                    }
                />
            )}
        </PageContent>
    );
};
